import { useState, useEffect, useContext } from "react";
import { UserContext } from "contexts/UserContext";
import User from "entities/User.entity";
import { getStorageCostNumber } from "components/Environment/utils";
import { getStoragePricing, isNvidiaEmail } from "utils/freeTierUtils";
import type { Environment } from "components/Environment/utils/types";

interface UseStorageCostPerHrReturn {
  storageCostPerHr: number;
  isLoadingCreatorInfo: boolean;
  creator: User | null;
}

export const useStorageCostPerHr = (
  environment: Environment,
): UseStorageCostPerHrReturn => {
  const [resolved, setResolved] = useState<{
    id: string | undefined;
    user: User | null;
  }>({ id: undefined, user: null });
  const userContext = useContext(UserContext);

  useEffect(() => {
    const creatorId = environment?.labels?.userId;
    if (!creatorId) return;
    let cancelled = false;
    userContext
      .getUser(creatorId)
      .then((user) => {
        if (cancelled) return;
        setResolved({ id: creatorId, user });
      })
      .catch(() => {
        if (cancelled) return;
        setResolved({ id: creatorId, user: null });
      });
    return () => {
      cancelled = true;
    };
  }, [environment?.labels?.userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const creator =
    resolved.id === environment?.labels?.userId ? resolved.user : null;
  const isLoadingCreatorInfo =
    !!environment?.labels?.userId &&
    resolved.id !== environment?.labels?.userId;
  const isCreatorEntitled = !!creator?.email && isNvidiaEmail(creator.email);

  const storageGiB = Number(
    getStorageCostNumber(environment?.instance?.disk_size || "") || 0,
  );
  const { totalCost: storageTotalCost, withinFreeTier } = getStoragePricing(
    environment?.instance?.instance_type_info,
    storageGiB,
  );
  const storageCostPerHr =
    isCreatorEntitled && withinFreeTier ? 0 : (storageTotalCost as number);

  return { storageCostPerHr, isLoadingCreatorInfo, creator };
};
